import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './styles.module.sass';
import { useSharedWaveform } from './hooks/hooks';
import { ECGPlot } from 'components';
import { GRID_COLOR, PLOT_WIDTH, PRINT_ECG_DURATION, STROKE_COLOR } from './constants/constants';
import { PrintEcg } from './print-ecg';
import { EcgDetails } from './components/components';
import { MAX_ECG_DURATION } from 'utils/constants';
import { Box } from '@mui/material';

export const SharedEcgScreen = () => {
  const location = useLocation();
  const encodeData = location.pathname.split('/').pop();
  const decodeData = window.atob(encodeData);
  const { ecgWaveForm, isLoading, accountData } = useSharedWaveform(decodeData);
  const plotRef = useRef<null | HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  const filteredSamples = useMemo(() => {
    if (!ecgWaveForm) {
      return [];
    }
    const maxSampleLength = ecgWaveForm.sampleRate * MAX_ECG_DURATION;

    return ecgWaveForm.samples.slice(-maxSampleLength);
  }, [ecgWaveForm]);

  const printSamples = useMemo(() => {
    if (!filteredSamples.length) {
      return [];
    }

    const ECGDuration = filteredSamples.length / ecgWaveForm.sampleRate;
    const oneSecondOffsetInPixels = PLOT_WIDTH / ECGDuration;

    const firstSampleIndex = Math.floor((offset / oneSecondOffsetInPixels) * ecgWaveForm.sampleRate);
    const lastSampleIndex = firstSampleIndex + PRINT_ECG_DURATION * ecgWaveForm.sampleRate;

    return filteredSamples.slice(firstSampleIndex, lastSampleIndex);
  }, [ecgWaveForm?.sampleRate, filteredSamples, offset]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => setOffset(e.currentTarget.scrollLeft);

  const generateContent = useCallback(() => {
    if (isLoading) {
      return <h2 className={styles.title}>Loading...</h2>;
    }
    if (ecgWaveForm) {
      return (
        <>
          <EcgDetails accountData={accountData} ecgWaveForm={ecgWaveForm} />
          <div onScroll={handleScroll} ref={plotRef} className={styles.plot_container}>
            <ECGPlot
              strokeColor={STROKE_COLOR}
              samples={filteredSamples}
              // drawChart={150}
              decreaseWave={40}
              // rate: 256 - good looking, 512 - real
              sampleRate={ecgWaveForm.sampleRate}
              // duration to show 5 real, 9 - fit good
              // durationToShow={15}
              durationToShow={undefined}
              // volts 5 - good-looking, 0.5 - real
              // millivolts={2}
              millivolts={undefined}
              // time 7 good-looking, 10 - real
              startTime={0}
              showGrid={true}
              signalStrokeWidth={1}
              smallGridStrokeWidth={undefined}
              bigGridStrokeWidth={undefined}
              // real : 1
              physicalGridSquareSize={1}
              full={undefined}
              backgroundFill='#fff'
              cellColor={GRID_COLOR}
            />
          </div>
          <PrintEcg accountData={accountData} samples={printSamples} ecgWaveForm={ecgWaveForm} />
        </>
      );
    }

    return (
      <h2 className={styles.title}>
        This link has expired due to security measures, please ask the originator to send you a new link
      </h2>
    );
  }, [accountData, ecgWaveForm, filteredSamples, isLoading, printSamples]);

  return <Box className={styles.container}>{generateContent()}</Box>;
};
